import { Switch } from '@headlessui/react';
import {
  EmptyState,
  PageHeader,
  TWButton,
  TWModal,
  TWDataTable,
} from '../../components/ui';
import { headerCls, EditAction, DataTableSkeleton } from '../../data/columns';
import React, { useEffect, useState } from 'react';
import {
  TrashIcon,
  PlusIcon,
  StarIcon,
  CreditCardIcon,
} from '@heroicons/react/24/outline';
import { StarIcon as StarSolid } from '@heroicons/react/20/solid';
import ModalDelete from '../../components/modal-delete/modal-delete';
import subscriptionApi from '../../services/subscriptionApi';
import './subscriptions.css';

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

const formatUSD = (value) =>
  (Number(value) || 0).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
  });

const Subscriptions = () => {
  const [subscriptionList, setSubscriptionList] = useState([]);
  const [deletedSubscription, setDeletedSubscription] = useState({});
  const [loading, setLoading] = useState({
    listLoading: true,
    upsertLoading: false,
    deleteLoading: false,
  });
  const [form, setForm] = useState({
    id: '',
    name: '',
    orderfee: null,
    monthlyfee: null,
    orderfeerate: null,
    isdefault: false,
  });

  const [dialogs, setDialogs] = useState({
    upsert: false,
    delete: false,
  });

  const [errors, setErrors] = useState({
    name: false,
    orderfee: false,
    monthlyfee: false,
    orderfeerate: false,
  });

  const [pagination, setPagination] = useState({
    pageNumber: 1,
    pageSize: 10,
    totalRecords: null,
  });

  const getSubscriptionList = async () => {
    setLoading((prev) => ({ ...prev, listLoading: true }));
    setSubscriptionList([]);
    try {
      const query = `?pageNumber=${pagination.pageNumber}&pageSize=${pagination.pageSize}`;
      const list = await subscriptionApi.list(query);
      if (list.data.data.length > 0) {
        setSubscriptionList(list.data.data);
        setPagination({
          pageNumber: list.data.pageNumber,
          pageSize: list.data.pageSize,
          totalRecords: list.data.totalRecords,
        });
      }
    } catch (error) {
      // ignore
    } finally {
      setLoading((prev) => ({ ...prev, listLoading: false }));
    }
  };

  useEffect(() => {
    getSubscriptionList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.pageNumber]);

  const onFormChange = (val, key) => {
    setForm((prev) => ({ ...prev, [key]: val }));
  };

  const onClickAdd = () => {
    setForm({
      id: '',
      name: '',
      orderfee: null,
      monthlyfee: null,
      orderfeerate: null,
      isdefault: false,
    });
    setDialogs((prev) => ({ ...prev, upsert: true }));
  };

  const onClickEdit = (val) => {
    setForm({
      id: val.id,
      name: val.name,
      orderfee: val.orderfee,
      monthlyfee: val.monthlyfee,
      orderfeerate: val.orderfeerate,
      isdefault: val.isdefault,
    });
    setDialogs((prev) => ({ ...prev, upsert: true }));
  };

  const onFormSubmit = async () => {
    if (
      !form.name ||
      !form.orderfee ||
      !form.monthlyfee ||
      !form.orderfeerate
    ) {
      setErrors({
        name: !form.name,
        orderfee: !form.orderfee,
        monthlyfee: !form.monthlyfee,
        orderfeerate: !form.orderfeerate,
      });
      return;
    }
    setErrors({
      name: false,
      orderfee: false,
      monthlyfee: false,
      orderfeerate: false,
    });

    try {
      setLoading((prev) => ({ ...prev, upsertLoading: true }));
      await subscriptionApi.upsert(form);
    } catch (error) {
      // ignore
    } finally {
      await getSubscriptionList();
      setLoading((prev) => ({ ...prev, upsertLoading: false }));
      setDialogs((prev) => ({ ...prev, upsert: false }));
    }
  };

  const onCloseUpsert = () => {
    setErrors({
      name: false,
      orderfee: false,
      monthlyfee: false,
      orderfeerate: false,
    });
    setDialogs((prev) => ({ ...prev, upsert: false }));
  };

  const onClickDelete = (sub) => {
    setDeletedSubscription(sub);
    setDialogs((prev) => ({ ...prev, delete: true }));
  };

  const onConfirmDelete = async () => {
    setLoading((prev) => ({ ...prev, deleteLoading: true }));
    try {
      await subscriptionApi.deleteById(deletedSubscription.id);
    } catch (error) {
      // ignore
    } finally {
      setDialogs((prev) => ({ ...prev, delete: false, upsert: false }));
      setLoading((prev) => ({ ...prev, deleteLoading: false }));
      await getSubscriptionList();
    }
  };

  const total = pagination.totalRecords || subscriptionList.length;

  return (
    <div className="subscriptions-shell min-h-[calc(100vh-4rem)] bg-white px-4 py-6 dark:bg-slate-900/40 sm:px-6 lg:px-8 lg:py-8">
      <div className="mx-auto max-w-5xl space-y-8">
        <PageHeader
          title="Subscriptions"
          description="Manage the pricing plans available to companies."
          actions={
            <TWButton
              variant="primary"
              icon={PlusIcon}
              label="New Subscription"
              onClick={onClickAdd}
            />
          }
        />

        {loading.listLoading ? (
          <DataTableSkeleton
            rows={5}
            headers={[
              { label: 'Name', w: 'w-32', shape: 'avatar' },
              { label: 'Per Order', w: 'w-16' },
              { label: 'Monthly Fee', w: 'w-16' },
              { label: 'Fee Rate', w: 'w-12' },
              { label: 'Default', w: 'w-12', shape: 'pill' },
              { align: 'right', w: 'w-8', shape: 'circle' },
            ]}
          />
        ) : subscriptionList.length === 0 ? (
          <EmptyState
            variant="dashed"
            icon={CreditCardIcon}
            title="No subscriptions yet"
            description="Create your first pricing plan to start onboarding companies."
            action={
              <TWButton
                variant="primary"
                icon={PlusIcon}
                label="New Subscription"
                onClick={onClickAdd}
              />
            }
          />
        ) : (
          <div className="reveal">
            <TWDataTable
              columns={[
                {
                  title: <span className={headerCls}>Name</span>,
                  key: 'name',
                  render: (_, sub) => (
                    <div className="flex min-w-0 items-center gap-3">
                      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-gradient-to-br from-blue-500 to-sky-500 text-white shadow-sm">
                        <CreditCardIcon className="h-5 w-5" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-semibold text-slate-900 dark:text-slate-100">
                          {sub.name}
                        </p>
                        {sub.isdefault && (
                          <p className="mt-0.5 flex items-center gap-1 text-xs text-amber-600 dark:text-amber-400">
                            <StarSolid className="h-3 w-3" />
                            Default plan
                          </p>
                        )}
                      </div>
                    </div>
                  ),
                },
                {
                  title: <span className={headerCls}>Per Order</span>,
                  key: 'orderfee',
                  render: (_, sub) => (
                    <span className="font-mono text-sm text-slate-700 dark:text-slate-200">
                      {formatUSD(sub.orderfee)}
                    </span>
                  ),
                },
                {
                  title: <span className={headerCls}>Monthly Fee</span>,
                  key: 'monthlyfee',
                  render: (_, sub) => (
                    <span className="font-mono text-sm text-slate-700 dark:text-slate-200">
                      {formatUSD(sub.monthlyfee)}
                    </span>
                  ),
                },
                {
                  title: <span className={headerCls}>Fee Rate</span>,
                  key: 'orderfeerate',
                  render: (_, sub) => (
                    <span className="font-mono text-sm text-slate-700 dark:text-slate-200">
                      {(Number(sub.orderfeerate) || 0).toFixed(2)}%
                    </span>
                  ),
                },
                {
                  title: <span className={headerCls}>Default</span>,
                  key: 'isdefault',
                  render: (_, sub) =>
                    sub.isdefault ? (
                      <span className="inline-flex items-center gap-1 rounded-md bg-emerald-50 px-2.5 py-0.5 text-xs font-semibold text-emerald-700 ring-1 ring-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-400 dark:ring-emerald-400/30">
                        <StarSolid className="h-3 w-3" />
                        Yes
                      </span>
                    ) : (
                      <span className="inline-flex items-center rounded-md bg-slate-100 px-2.5 py-0.5 text-xs font-medium text-slate-500 dark:bg-slate-700/60 dark:text-slate-400">
                        No
                      </span>
                    ),
                },
                {
                  title: <span className="sr-only">Actions</span>,
                  key: 'actions',
                  align: 'right',
                  width: 60,
                  render: (_, sub) => (
                    <EditAction
                      onClick={() => onClickEdit(sub)}
                      label="Edit subscription"
                    />
                  ),
                },
              ]}
              data={subscriptionList}
              rowKey="id"
              pagination={
                total > pagination.pageSize
                  ? {
                      current: pagination.pageNumber,
                      pageSize: pagination.pageSize,
                      total: pagination.totalRecords,
                      showTotal: (t, range) =>
                        `Showing ${range[0]} to ${range[1]} of ${t} subscriptions`,
                      onChange: (page) =>
                        setPagination((prev) => ({
                          ...prev,
                          pageNumber: page,
                        })),
                      showSizeChanger: false,
                    }
                  : undefined
              }
            />
          </div>
        )}
      </div>

      <TWModal
        open={dialogs.upsert}
        onClose={onCloseUpsert}
        title={form.id ? 'Edit Subscription' : 'New Subscription'}
        description="Set fees and availability for this pricing plan."
        size="xl"
        showCloseButton
        dismissible={false}
        bodyClassName="p-0"
        footer={
          <div className="flex items-center justify-end gap-2">
            {form.id && (
              <TWButton
                variant="danger"
                icon={TrashIcon}
                label={loading.deleteLoading ? 'Deleting…' : 'Delete'}
                loading={loading.deleteLoading}
                disabled={loading.deleteLoading}
                onClick={() => onClickDelete(form)}
                className="mr-auto"
              />
            )}
            <TWButton
              variant="default"
              label="Cancel"
              disabled={loading.upsertLoading}
              onClick={onCloseUpsert}
            />
            <TWButton
              variant="primary"
              label={form.id ? 'Save Changes' : 'Add Subscription'}
              disabled={loading.upsertLoading}
              loading={loading.upsertLoading}
              onClick={onFormSubmit}
            />
          </div>
        }
      >
        <div className="space-y-4 px-6 py-5">
          <div>
            <label
              htmlFor="name"
              className="mb-1 block text-[0.7rem] font-semibold uppercase tracking-[0.12em] text-slate-500 dark:text-slate-400"
            >
              Name
            </label>
            <input
              id="name"
              value={form.name || ''}
              onChange={(e) => onFormChange(e.target.value, 'name')}
              type="text"
              placeholder="Starter, Pro, Enterprise…"
              className={classNames(
                'block w-full rounded-md border bg-white px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 dark:bg-slate-900 dark:text-slate-100 dark:placeholder:text-slate-500',
                errors.name
                  ? 'border-rose-300 focus:border-rose-400 focus:ring-rose-400/20 dark:border-rose-400/50'
                  : 'border-slate-200 focus:border-blue-500 focus:ring-blue-500/20 dark:border-slate-700 dark:focus:border-sky-400 dark:focus:ring-sky-400/20',
              )}
            />
            {errors.name && (
              <p className="mt-1 text-xs text-rose-600">
                Name can not be empty
              </p>
            )}
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label
                htmlFor="orderfee"
                className="mb-1 block text-[0.7rem] font-semibold uppercase tracking-[0.12em] text-slate-500 dark:text-slate-400"
              >
                Per Order Charge
              </label>
              <div className="relative">
                <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-sm text-slate-400">
                  $
                </span>
                <input
                  id="orderfee"
                  value={form.orderfee ?? ''}
                  onChange={(e) => onFormChange(e.target.value, 'orderfee')}
                  type="number"
                  step="0.01"
                  placeholder="0.00"
                  className={classNames(
                    'block w-full rounded-md border bg-white py-2 pl-7 pr-3 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 dark:bg-slate-900 dark:text-slate-100 dark:placeholder:text-slate-500',
                    errors.orderfee
                      ? 'border-rose-300 focus:border-rose-400 focus:ring-rose-400/20 dark:border-rose-400/50'
                      : 'border-slate-200 focus:border-blue-500 focus:ring-blue-500/20 dark:border-slate-700 dark:focus:border-sky-400 dark:focus:ring-sky-400/20',
                  )}
                />
              </div>
              {errors.orderfee && (
                <p className="mt-1 text-xs text-rose-600">
                  Per Order Charge can not be empty
                </p>
              )}
            </div>

            <div>
              <label
                htmlFor="monthlyfee"
                className="mb-1 block text-[0.7rem] font-semibold uppercase tracking-[0.12em] text-slate-500 dark:text-slate-400"
              >
                Monthly Fee
              </label>
              <div className="relative">
                <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-sm text-slate-400">
                  $
                </span>
                <input
                  id="monthlyfee"
                  value={form.monthlyfee ?? ''}
                  onChange={(e) => onFormChange(e.target.value, 'monthlyfee')}
                  type="number"
                  step="0.01"
                  placeholder="0.00"
                  className={classNames(
                    'block w-full rounded-md border bg-white py-2 pl-7 pr-3 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 dark:bg-slate-900 dark:text-slate-100 dark:placeholder:text-slate-500',
                    errors.monthlyfee
                      ? 'border-rose-300 focus:border-rose-400 focus:ring-rose-400/20 dark:border-rose-400/50'
                      : 'border-slate-200 focus:border-blue-500 focus:ring-blue-500/20 dark:border-slate-700 dark:focus:border-sky-400 dark:focus:ring-sky-400/20',
                  )}
                />
              </div>
              {errors.monthlyfee && (
                <p className="mt-1 text-xs text-rose-600">
                  Monthly Fee can not be empty
                </p>
              )}
            </div>
          </div>

          <div>
            <label
              htmlFor="orderfeerate"
              className="mb-1 block text-[0.7rem] font-semibold uppercase tracking-[0.12em] text-slate-500 dark:text-slate-400"
            >
              Order Fee Rate
            </label>
            <div className="relative">
              <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-sm text-slate-400">
                %
              </span>
              <input
                id="orderfeerate"
                value={form.orderfeerate ?? ''}
                onChange={(e) => onFormChange(e.target.value, 'orderfeerate')}
                type="number"
                step="0.01"
                placeholder="0.00"
                className={classNames(
                  'block w-full rounded-md border bg-white py-2 pl-7 pr-3 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 dark:bg-slate-900 dark:text-slate-100 dark:placeholder:text-slate-500',
                  errors.orderfeerate
                    ? 'border-rose-300 focus:border-rose-400 focus:ring-rose-400/20 dark:border-rose-400/50'
                    : 'border-slate-200 focus:border-blue-500 focus:ring-blue-500/20 dark:border-slate-700 dark:focus:border-sky-400 dark:focus:ring-sky-400/20',
                )}
              />
            </div>
            {errors.orderfeerate && (
              <p className="mt-1 text-xs text-rose-600">
                Order Fee Rate can not be empty
              </p>
            )}
          </div>

          <label className="flex cursor-pointer items-start gap-3 rounded-md border border-slate-200 bg-slate-50/50 p-3 transition hover:border-slate-300 dark:border-slate-700 dark:bg-slate-900/50 dark:hover:border-slate-600">
            <Switch
              checked={form.isdefault}
              onChange={(val) => onFormChange(val, 'isdefault')}
              className={classNames(
                form.isdefault
                  ? 'bg-blue-600 dark:bg-sky-500'
                  : 'bg-slate-300 dark:bg-slate-600',
                'relative mt-0.5 inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:ring-offset-1',
              )}
            >
              <span
                aria-hidden="true"
                className={classNames(
                  form.isdefault ? 'translate-x-4' : 'translate-x-0',
                  'pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out',
                )}
              />
            </Switch>
            <div className="min-w-0">
              <p className="flex items-center gap-1 text-sm font-medium text-slate-900 dark:text-slate-100">
                <StarIcon className="h-4 w-4 text-amber-500" />
                Set as default plan
              </p>
              <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
                New companies will start on the default plan.
              </p>
            </div>
          </label>
        </div>
      </TWModal>

      <ModalDelete
        title="Delete Subscription"
        loading={loading.deleteLoading}
        open={dialogs.delete}
        onCloseRequest={() =>
          setDialogs((prev) => ({ ...prev, delete: false }))
        }
        onOk={onConfirmDelete}
        item={deletedSubscription}
      />
    </div>
  );
};

export default Subscriptions;
